import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import subscriptionService from '../services/subscription';
import timesheetService, { type TimesheetStatus } from '../services/timesheet';
import leaveService, { type LeaveOnDay } from '../services/leave';
import { ApiError } from '../types/api';
import workScheduleService, { type DaySchedule } from '../services/workScheduleService';
import { requestLocationWithDisclosure } from '../services/locationGate';
import { getOrgTimezone, getTodayInTz, getTodayDowInTz, dateTimeLocalToISO } from '../utils/timezone';
import { isOrgSuperAdmin } from '../utils/auth';
import type { ActiveModule, UserLicense } from '../types/subscription';

const TIMESHEET_MODULE_KEY = 'timesheet';
const WINDOW_MS = 60 * 60 * 1000; // ±1 hour window
const TICK_INTERVAL_MS = 60 * 1000; // re-evaluate every minute

export interface TimesheetClockWidgetState {
  showClockIn: boolean;
  showClockOut: boolean;
  loading: boolean;
  /**
   * The request failed and is being tried again.
   *
   * Clock calls get up to 45 s across three attempts on a weak link, and a
   * button that shows nothing but a spinner for that long reads as a hung app.
   * The operator then presses it again, or walks off believing they are on the
   * clock. Saying "still trying" costs a word and removes both.
   */
  retrying: boolean;
  /** Hours elapsed in the current active session (0 if not clocked in) */
  sessionHours: number;
  /**
   * Why the last clock-in/out attempt failed, or null.
   *
   * These handlers used to swallow every error, so a rejected clock-out looked
   * exactly like a button that did nothing: no spinner left, no message, no
   * change. In production that had people pressing Clock Out over and over at
   * the end of a shift while the server returned the same refusal each time.
   * A refusal the server bothered to word has to reach the person holding the
   * phone.
   */
  error: string | null;
  dismissError: () => void;
  /**
   * Approved leave standing in the way of clocking in, or null.
   *
   * Leave is approved in advance and writes its timesheet rows at approval
   * time. When the employee's plans change and they come to work, the clock-in
   * is refused rather than allowed to create a session that can never be closed
   * — so this has to be answerable, not just reportable.
   */
  leaveConflict: LeaveOnDay | null;
  /** Release that day's leave (crediting it back) and clock in. */
  confirmReleaseLeave: () => Promise<void>;
  dismissLeaveConflict: () => void;
  /** Part of today is already booked as leave, but clocking in was fine. */
  leaveNotice: LeaveOnDay | null;
  dismissLeaveNotice: () => void;
  handleClockIn: () => Promise<void>;
  handleClockOut: (skipLunch?: boolean) => Promise<void>;
}

/**
 * Drives the dynamic Clock In / Clock Out button shown in the page header.
 *
 * Shows Clock In when:
 *   - The org has an active timesheet subscription
 *   - The user holds a timesheet licence (or is super_admin)
 *   - Today is a scheduled working day
 *   - The current time is within 1 hour before or after the scheduled start time
 *   - The user is not already clocked in
 *
 * Clock-in grace period (server-side): clocking in up to the grace window
 * *before* the scheduled start snaps the recorded time forward to the scheduled
 * start.  Clocking in *after* the scheduled start records the actual time (no
 * backdating).
 *
 * Shows Clock Out when:
 *   - Same licence/schedule conditions as above
 *   - The user is currently clocked in
 *   - The current time is within 1 hour before (or any time after) the scheduled end time
 *
 * Clock-out grace period (server-side): clocking out up to the grace window
 * *after* the scheduled end snaps the recorded time back to the scheduled end.
 * Clocking out *before* the scheduled end records the actual time (no
 * rounding up of hours).
 */
export function useTimesheetClockWidget(): TimesheetClockWidgetState {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [status, setStatus] = useState<TimesheetStatus | null>(null);
  const [todaySchedule, setTodaySchedule] = useState<DaySchedule | null>(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaveConflict, setLeaveConflict] = useState<LeaveOnDay | null>(null);
  const [leaveNotice, setLeaveNotice] = useState<LeaveOnDay | null>(null);
  const [initialized, setInitialized] = useState(false);
  // Ticks every minute so the time-window check is re-evaluated automatically.
  const [, setTick] = useState(0);

  const timezone = getOrgTimezone(user);
  const isSuperAdmin = isOrgSuperAdmin(user);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await timesheetService.getStatus();
      if (res.success) setStatus(res.data ?? null);
    } catch {
      // non-fatal
    }
  }, []);

  // One-time initialisation: fetch access, schedule, and current clock status.
  useEffect(() => {
    if (!user?.id || !user?.organisation_id) return;

    let cancelled = false;

    async function init() {
      try {
        const [modulesRes, licensesRes, scheduleRes, statusRes] = await Promise.all([
          subscriptionService.getActiveModules(),
          subscriptionService.getMyLicenses(),
          workScheduleService.getUserSchedule(user!.id),
          timesheetService.getStatus(),
        ]);

        if (cancelled) return;

        const modules: ActiveModule[] = modulesRes.success ? (modulesRes.data ?? []) : [];
        const licenses: UserLicense[] = licensesRes.success ? (licensesRes.data ?? []) : [];

        const orgHasModule = subscriptionService.hasModuleAccess(modules, TIMESHEET_MODULE_KEY);
        const userHasLicense =
          isSuperAdmin || subscriptionService.hasLicense(licenses, TIMESHEET_MODULE_KEY, user?.organisation_id);

        setHasAccess(orgHasModule && userHasLicense);

        if (statusRes.success) setStatus(statusRes.data ?? null);

        if (scheduleRes.success && scheduleRes.data) {
          const todayDow = getTodayDowInTz(timezone);
          const entry = scheduleRes.data.find(d => d.day_of_week === todayDow) ?? null;
          setTodaySchedule(entry);
        }
      } catch {
        // non-fatal — widget simply stays hidden
      } finally {
        if (!cancelled) setInitialized(true);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.organisation_id, isSuperAdmin, timezone]);

  // Periodic tick so the ±1-hour window is re-evaluated each minute.
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const dismissError = useCallback(() => setError(null), []);

  // The axios interceptor normalises every failure into an ApiError carrying the
  // server's own wording, so prefer that over anything invented here.
  const messageFor = (err: unknown): string => {
    const msg = (err as { message?: unknown })?.message;
    return typeof msg === 'string' && msg.trim()
      ? msg
      : 'Something went wrong. Please try again.';
  };

  const dismissLeaveConflict = useCallback(() => setLeaveConflict(null), []);
  const dismissLeaveNotice = useCallback(() => setLeaveNotice(null), []);

  // Shared by the ordinary clock-in and the retry after leave is released, so
  // the two cannot drift apart on location handling or error reporting.
  const doClockIn = useCallback(async () => {
    const loc = await requestLocationWithDisclosure();
    const res = await timesheetService.clockIn({
      latitude: loc?.coords.latitude,
      longitude: loc?.coords.longitude,
      accuracy: loc?.coords.accuracy,
    }, { onRetry: () => setRetrying(true) });
    if (res.success) {
      setLeaveConflict(null);
      // Present when part of the day is booked off but clocking in was fine.
      setLeaveNotice(res.leave_notice ?? null);
      await refreshStatus();
    }
  }, [refreshStatus]);

  const handleClockIn = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    setLeaveNotice(null);
    try {
      await doClockIn();
    } catch (err) {
      // A day already covered by approved leave is a question, not a failure.
      // Surfacing it as a red banner would tell the employee they cannot start
      // work, when what they need is to be asked which of the two is true.
      if (err instanceof ApiError && err.code === 'LEAVE_ON_THIS_DAY') {
        const leave = err.details?.leave as LeaveOnDay | undefined;
        if (leave) {
          setLeaveConflict(leave);
          return;
        }
      }
      setError(messageFor(err));
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  }, [loading, doClockIn]);

  const confirmReleaseLeave = useCallback(async () => {
    if (loading || !leaveConflict) return;
    setLoading(true);
    setError(null);
    try {
      await leaveService.releaseDay(
        leaveConflict.leave_request_id,
        leaveConflict.date,
        'Worked this day instead'
      );
      // Only now is the day clear. Clocking in first and releasing after would
      // leave an unclosable session behind if the release failed.
      await doClockIn();
    } catch (err) {
      setError(messageFor(err));
      setLeaveConflict(null);
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  }, [loading, leaveConflict, doClockIn]);

  const handleClockOut = useCallback(async (skipLunch?: boolean) => {
    if (loading) return;
    setLoading(true);
    try {
      const loc = await requestLocationWithDisclosure();
      const res = await timesheetService.clockOut({
        latitude: loc?.coords.latitude,
        longitude: loc?.coords.longitude,
        accuracy: loc?.coords.accuracy,
        skip_lunch_deduction: skipLunch,
      }, { onRetry: () => setRetrying(true) });
      if (res.success) await refreshStatus();
    } catch (err) {
      setError(messageFor(err));
      // The refusal may be about state this client no longer has right (an
      // overlapping entry, a session closed elsewhere). Re-read rather than
      // leaving a stale Clock Out button next to a contradicting message.
      await refreshStatus();
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  }, [loading, refreshStatus]);

  const sessionHours = status?.activeSessions?.[0]?.current_hours ?? 0;

  const noShow: TimesheetClockWidgetState = {
    showClockIn: false,
    showClockOut: false,
    loading,
    retrying,
    sessionHours,
    error,
    dismissError,
    leaveConflict,
    confirmReleaseLeave,
    dismissLeaveConflict,
    leaveNotice,
    dismissLeaveNotice,
    handleClockIn,
    handleClockOut,
  };

  if (!initialized || !hasAccess || !todaySchedule || !todaySchedule.is_working_day) {
    return noShow;
  }

  const isClocked = status?.status === 'clocked_in';
  const now = Date.now();
  const today = getTodayInTz(timezone);

  let showClockIn = false;
  let showClockOut = false;

  if (!isClocked && todaySchedule.start_time) {
    const startISO = dateTimeLocalToISO(`${today}T${todaySchedule.start_time}`, timezone);
    const startMs = new Date(startISO).getTime();
    showClockIn = Math.abs(now - startMs) <= WINDOW_MS;
  }

  // Show Clock Out from 1 hour before the scheduled end time onwards.
  // Clocking out before the scheduled end records the actual time (no rounding
  // up); clocking out slightly after the scheduled end snaps back to the
  // scheduled end via the server-side grace period.
  if (isClocked && todaySchedule.end_time) {
    const endISO = dateTimeLocalToISO(`${today}T${todaySchedule.end_time}`, timezone);
    const endMs = new Date(endISO).getTime();
    showClockOut = now >= endMs - WINDOW_MS;
  }

  return {
    showClockIn, showClockOut, loading, retrying, sessionHours,
    error, dismissError,
    leaveConflict, confirmReleaseLeave, dismissLeaveConflict,
    leaveNotice, dismissLeaveNotice,
    handleClockIn, handleClockOut,
  };
}
